/**
 * Common helper functions for the application.
 */

/**
 * Key for storing user credentials in local storage.
 */
const USER_CREDENTIALS_KEY = '__user_credentials_rhomtrip';

/**
 * User roles constants.
 */
export const ROLES = Object.freeze({
    SUPER_ADMIN: 'super_admin',
    ADMIN: 'admin',
    MANAGER: 'manager',
    USER: 'user',
    SUPPORT: 'support'
});

/** 
 * Role hierarchy from highest to lowest.
 */
export const ROLE_HIERARCHY = [
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.MANAGER,
    ROLES.SUPPORT,
    ROLES.USER
];

/**
 * Permissions mapping for different user roles.
 */
export const PERMISSIONS = {
    "SUPER_ADMIN": {
        "DASHBOARD": ["READ"],
        "RESERVATIONS": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "MARK_AS_PAID", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "QUOTES": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "CUSTOMERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "PARTNERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "RESELLERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "SUPPLIERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "BRANCHES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "AREAS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "VEHICLES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "RATES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "EXPERIENCES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "TEAM": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "TOOLS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "DESTINATIONS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT"],
        "TAGS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"]
    },
    "ADMIN": {
        "DASHBOARD": ["READ"],
        "RESERVATIONS": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "MARK_AS_PAID", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "QUOTES": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "CUSTOMERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "PARTNERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "RESELLERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "SUPPLIERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "BRANCHES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "AREAS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "VEHICLES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "RATES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "EXPERIENCES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "TEAM": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "TOOLS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "DESTINATIONS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT"],
        "TAGS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"]
    },
    "MANAGER": {
        "DASHBOARD": ["READ"],
        "RESERVATIONS": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "MARK_AS_PAID", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "QUOTES": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "CUSTOMERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "PARTNERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "RESELLERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "SUPPLIERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "BRANCHES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "AREAS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "VEHICLES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "RATES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "EXPERIENCES": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "TEAM": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "TOOLS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"],
        "DESTINATIONS": ["CREATE", "READ",],
        "TAGS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE"]
    },
    "SUPPORT": {
        "DASHBOARD": [],
        "RESERVATIONS": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "MARK_AS_PAID", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "QUOTES": ["CREATE", "READ", "UPDATE", "DELETE", "CHANGE_STATUS", "SEND_PAYMENT_LINK", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "CUSTOMERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "PARTNERS": ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "ADD_HISTORY_NOTE", "RESEND_EMAIL"],
        "RESELLERS": ["CREATE", "READ", "UPDATE", "RESEND_EMAIL", "EXPORT", "ADD_HISTORY_NOTE"],
        "SUPPLIERS": ["READ"],
        "BRANCHES": ["READ"],
        "AREAS": ["READ"],
        "VEHICLES": ["READ"],
        "RATES": ["READ"],
        "EXPERIENCES": ["READ"],
        "TEAM": ["READ"],
        "TOOLS": ["READ"],
        "DESTINATIONS": ["READ"],
        "TAGS": ["READ"]
    },
    "USER": {
        "DASHBOARD": [],
        "RESERVATIONS": ["READ"],
        "QUOTES": ["READ"],
        "CUSTOMERS": ["READ"],
        "PARTNERS": ["READ"],
        "RESELLERS": ["READ"],
        "SUPPLIERS": ["READ"],
        "BRANCHES": ["READ"],
        "AREAS": ["READ"],
        "VEHICLES": ["READ"],
        "RATES": ["READ"],
        "EXPERIENCES": ["READ"],
        "TEAM": ["READ"],
        "TOOLS": ["READ"],
        "DESTINATIONS": ["READ"],
        "TAGS": ["READ"]
    }
};

/**
 * Reservation events constants.
 */
export const RESERVATION_ACTION_EVENTS = Object.freeze({
    RESERVATION_CREATED: "reservation_created",
    RESERVATION_UPDATED: "reservation_updated",
    RESERVATION_CONFIRMED: "reservation_confirmed",
    RESERVATION_REJECTED: "reservation_rejected",
    RESERVATION_CANCELED: "reservation_canceled",
    RESERVATION_DELETED: "reservation_deleted",
    RESERVATION_PAID: "reservation_paid",
    RESERVATION_PAYMENT_STATUS_CHANGED: "reservation_payment_status_changed"
});

/**
 * Payment events constants.
 */
export const PAYMENT_ACTION_EVENTS = Object.freeze({
    PAYMENT_SUCCESS: "payment_success",
    PAYMENT_FAILED: "payment_failed",
    PAYMENT_PENDING: "payment_pending"
});

/**
 * Combined action events from reservation and payment events.
 */
export const ACTION_EVENTS = Object.freeze([
    ...Object.values(RESERVATION_ACTION_EVENTS),
    ...Object.values(PAYMENT_ACTION_EVENTS)
]);


/**
 * Check if the error is an unauthorized action (401).
 * @param {Object} error - The error object.
 * @returns {boolean} - True if unauthorized, false otherwise.
 */
export function unauthorizedAction(error) {
    if (error.response) {
        let { data } = error.response;
        if (data.code === 401) {
            return true;
        } else {
            return false;
        }
    } else if (error.request) {
        return false;
    } else {
        return false;
    }
}

/**
 * Simple throttle function that executes a passed function only once in the specified timeout
 * @param handlerFunc
 * @param [timeout] the throttle interval
 */
export function throttle(handlerFunc, timeout = 66) {
    let resizeTimeout;
    if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
            resizeTimeout = null;
            handlerFunc();
            // The actualResizeHandler will execute at a rate of 15fps
        }, timeout);
    }
}

/**
 * Navigate back in history or to the home page if no history exists.
 * @param {Object} self - The Vue component instance.
 */
export function goBack(self) {
    if (window.history.length > 1) {
        self.$router.back();
    } else {
        self.$router.push('/');
    }
}

/**
 * Check if the error requires signing out (401).
 * @param {Object} error - The error object.
 * @returns {boolean} - True if sign out is required, false otherwise.
 */
export function setSigOut(error) {
    if (error.response) {
        let { data } = error.response;
        if (data.code === 401) {
            return true;
        } else {
            return false;
        }
    } else if (error.request) {
        return false;
    } else {
        return false;
    }
}

/**
 * Check if a user is authenticated to perform a specific action on a module based on their role.
 * @param {string} module - The module name.
 * @param {string} role - The user's role.
 * @param {string} action - The action to check (e.g., "CREATE", "READ", "UPDATE", "DELETE").
 * @returns {boolean} - True if the user is authorized, false otherwise.
 */
export function isUserAuthorized(module, role, action) {
    const actionsByRole = getActionsByRoleAndModule(role, module);
    let isAuthorized = actionsByRole.includes(action);
    return isAuthorized;
}

/** * Get the allowed actions for a given role and module.
 * @param {string} role - The user's role.
 * @param {string} module - The module name.
 * @returns {Array} - List of allowed actions.
 */
export function getActionsByRoleAndModule(role, module) {
    if (role === "super_admin") {
        return PERMISSIONS.SUPER_ADMIN[module];
    } else if (role === "admin") {
        return PERMISSIONS.ADMIN[module];
    } else if (role === "manager") {
        return PERMISSIONS.MANAGER[module];
    } else if (role === "support") {
        return PERMISSIONS.SUPPORT[module];
    } else if (role === "user") {
        return PERMISSIONS.USER[module];
    }
    return [];
}

/** * Validate email format.
 * @param {string} email - The email string.
 * @returns {boolean} - True if email is valid, false otherwise.
 */
export function validateEmail(email) {
    return /^[\w+\-\.]+@[\w-]+(\.[a-zA-Z]{2,}){1,}$/.test(email);
}

/** * Retrieve user credentials from local storage.
 * @returns {Object|null} - The user credentials object or null if not found.
 */
export function getCredentials() {
    const credentials = localStorage.getItem(USER_CREDENTIALS_KEY);
    return credentials ? JSON.parse(credentials) : null;
}

/** * Store user credentials in local storage.
 * @param {Object} credentials - The user credentials object.
 *
 */
export function setCredentials(credentials) {
    localStorage.setItem(USER_CREDENTIALS_KEY, JSON.stringify(credentials));
}

/** * Remove user credentials from local storage.
 */
export function removeCredentials() {
    localStorage.removeItem(USER_CREDENTIALS_KEY);
}

/** * Get the current user's role from stored credentials.
 * @returns {string|null} - The user's role or null if not authenticated.
 */
export function getUserRole() {
    const credentials = getCredentials();
    return credentials ? credentials.role : null;
}

/** * Get ID parameter pattern for routes.
 * @param {string} paramName - The name of the parameter. Default is "id".
 * @returns {string} - The route parameter pattern for a 24-character hexadecimal ID.
 */
export function idParam(paramName = "id") {
    return `:${paramName}([a-fA-F0-9]{24})`;
}

/**
 * Checks whether a client `type` corresponds to a partner (travel agency or agent).
 * @param {string} type - The client `type` field to check.
 * @returns {boolean} True if the type is `travel_agency` or `travel_access`.
 */
export function isPartner(type) {
    return ['travel_agency', 'travel_access'].indexOf(type) > -1;
}

/**
 * Check if the error is an unauthorized action (401).
 * @param {Object} error - The error object.
 * @returns {boolean} - True if unauthorized, false otherwise.
 */
export function isUnauthorizedError(error) {
    if (error.response) {
        let { data } = error.response;
        if (data.code === 401) {
            return true;
        }
    }
    return false;
}
